import React, { useState, useEffect, useRef, useCallback } from "react";
import useFilters from "hooks/useFilters";
import { useHistory } from "react-router-dom";
import { useStore } from "../../../../../store/context";
import i18n from "i18n";

import * as MobileHelpers from "helpers/Mobile";
import { EventRepository } from "helpers/EventRepository";
import { StockService } from "../../../../../services/StockService";
import { BusinessNodeService } from "services/BusinessNodeService";
import { LocationService } from "services/LocationService";
import AuditoriaFilterPanel from "../AuditoriaFilterPanel";

import styles from "./ViewStockAudit.module.scss";
import AdminLayout from "../../../../../components/MainApp/layouts/DesktopLayout";
import AuditTable from "./Table";

import headers from "./headers";
import Pagination from "../../../../../components/MainApp/atoms/Pagination";
import Loader from "../../../../../components/MainApp/atoms/Loader";
import Tooltip from "components/MainApp/atoms/Tooltip";

import excel from "assets/images/excel.svg";
import excelwhite from "assets/images/excelwhite.svg";

import MainNotification from "../../../../../components/MainApp/atoms/Notification";
import { NotFoundItems } from "../../../components/NotFoundItems";
import { isMobile } from "helpers/Mobile";
import { capitalize } from "helpers/utils/utils_general";

const initialFilters = {
  page_size: 10,
  search: "",
  ordering: "-date",
  enabled: undefined,
  categories: undefined,
  brand: undefined,
  locations: undefined,
  status: undefined,
  modifiedTrue: false,
  modifiedFalse: false,
  dateFrom: null,
  dateTo: null,
  search: "",
  page: 1
};

const filterTypeMap = {
  categories: { name: "categories", type: "dropdown" },
  brand: { name: "brand", type: "dropdown" },
  enabled: {
    name: "enabled",
    type: "twoOption",
    default_value: undefined
  },
  ordering: {
    name: "ordering",
    type: "twoOption",
    default_value: "-date"
  }
};

const filterDataSource = [
  {
    type: "dropdown",
    options: [],
    label: capitalize(i18n.t("categories")),
    firstOptionLabel: i18n.t("all.categories"),
    name: "categories"
  },
  {
    type: "dropdown",
    options: [],
    label: capitalize(i18n.t("brand")),
    firstOptionLabel: i18n.t("all.brands"),
    name: "brand"
  },
  {
    type: "twoOption",
    options: [
      {
        selected: false,
        label: i18n.t("enabled"),
        value: true
      },
      {
        selected: false,
        label: i18n.t("disabled"),
        value: false
      }
    ],
    label: i18n.t("product.status"),
    firstOptionLabel: i18n.t("all.m"),
    name: "enabled"
  }
];

export const ProductsView = () => {
  const [productFilters, setProductFilters] = useState();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [noproducts, setNoProducts] = useState(false);
  const [filterData, setFilterData] = useState(filterDataSource);
  const [page, setPage] = useState(1);

  const [selectedOptions, setSelectedOptions] = useState([]);
  const [locationIds, setLocationIds] = useState([]);
  const [auditStatus, setAuditStatus] = useState(undefined);
  const [totalAudits, setTotalAudits] = useState();
  const [auditModifiedTrue, setAuditModifiedTrue] = useState(false);
  const [auditModifiedFalse, setAuditModifiedFalse] = useState(false);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  const [products_name, setProducts_name] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const myRef = useRef(0);
  const suppressFilterEffectRef = useRef(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isScrollingLoading, setIsScrollingLoading] = useState(false);
  const [loadingName, setLoadinNameg] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState("1");
  const [isFiltering, setIsFiltering] = useState(false);

  const isMobileDevice = MobileHelpers.isMobile;

  let scrolling = false;

  const [
    filters,
    setFilters,
    selectedFiltersAll,
    selectedFiltersByType,
    handleSearch,
    resetOrdering,
    handleFiltersChangeOrdering,
    applied,
    setItems,
    items,
    pager,
    setPager,
    header,
    setHeader,
    mobilePanelOpen,
    setMobilePanelOpen
  ] = useFilters(
    initialFilters,
    productFilters,
    filterTypeMap,
    isMobile,
    headers
  );

  const filtersSafe = filters || initialFilters;
  const headerSafe = header || headers;

  const [auditsItems, setAuditsItems] = useState([]);

  const pageSize = 10;
  const pageQuantity = Number.isFinite(Number(pager?.countPages))
    ? Math.max(1, Math.floor(Number(pager.countPages)))
    : Math.max(1, Math.ceil(totalAudits / pageSize));

  const currentPage = Math.max(1, Math.floor(Number(page) || 1));

  const getFilterSummaryText = () => {
    if (!isMobile) return null;

    const summary = [];

    // 1. (Locations)
    if (locationIds && locationIds.length > 0) {
      const selectedLocationNames = selectedOptions
        .filter(opt => opt && (opt.name || opt.label))
        .map(opt => opt.name || opt.label);

      let locationText;

      if (selectedLocationNames.length > 0) {
        locationText = selectedLocationNames.join(" | ");
      } else {
        const firstSelectedName =
          selectedOptions[0]?.name || selectedOptions[0]?.label;
        locationText =
          firstSelectedName || `${locationIds.length} ${i18n.t("locations")}`;
      }

      summary.push(`${i18n.t("locations")}: ${locationText}`);
    }

    // 2. (Status)
    if (auditStatus !== undefined) {
      const statusText = auditStatus
        ? i18n.t("complete")
        : i18n.t("incomplete");
      summary.push(`${i18n.t("status")}: ${statusText}`);
    }

    // 3. (Modified True/False)
    if (auditModifiedTrue !== auditModifiedFalse) {
      const modifiedText = auditModifiedTrue
        ? i18n.t("modified")
        : i18n.t("no.modified");
      summary.push(`${i18n.t("modified")}: ${modifiedText}`);
    }

    // 4. (Date Range)
    if (dateFrom || dateTo) {
      let dateText = `${i18n.t("date")}: `;
      if (dateFrom && dateTo) {
        dateText += `${dateFrom} - ${dateTo}`;
      } else if (dateFrom) {
        dateText += `${i18n.t("from")} ${dateFrom}`;
      } else if (dateTo) {
        dateText += `${i18n.t("to")} ${dateTo}`;
      }
      summary.push(dateText);
    }

    // 5. (Search)
    if (filtersSafe?.search && filtersSafe.search.trim()) {
      summary.push(`${i18n.t("search")}: ${filtersSafe.search.trim()}`);
    }

    if (summary.length === 0) return null;

    return (
      <div className={styles["filter-summary-chips-container"]}>
        {summary.map((text, index) => (
          <div key={index} className={styles["filter-chip"]}>
            {text}
          </div>
        ))}
      </div>
    );
  };

  const filter = (firstLoad = false, overrides = {}) => {
    const pageToUse = overrides.page ?? page;
    const isAppendMode = isMobile && pageToUse > 1 && !firstLoad;

    if (!isAppendMode) {
      setAuditsItems([]);

      // BEFORE SUBMIT
      document.body.style.cursor = "wait";
      setSubmitted(true);
    } else {
    }

    const pageSizeToUse = filtersSafe.page_size || initialFilters.page_size;
    const safeOverrides = overrides || {};

    const params = {
      page: pageToUse,
      page_size: pageSizeToUse,
      ordering: safeOverrides.ordering ?? filtersSafe?.ordering ?? "-date"
    };

    // Apply location filter
    const locationsList = safeOverrides.locations || locationIds;
    if (locationsList && locationsList.length > 0) {
      params["locations"] = locationsList.join(",");
    }

    // Apply status filter
    const statusToUse =
      "status" in safeOverrides ? safeOverrides.status : auditStatus;

    if (statusToUse !== undefined) {
      params["status"] = statusToUse;
    }

    // Apply modified_status filter
    const mTrue = safeOverrides.modifiedTrue ?? auditModifiedTrue;
    const mFalse = safeOverrides.modifiedFalse ?? auditModifiedFalse;
    if (mTrue !== mFalse) {
      params["modified_status"] = mTrue ? true : false;
    }

    // Apply date filters
    const from =
      "dateFrom" in safeOverrides ? safeOverrides.dateFrom : dateFrom;

    const to = "dateTo" in safeOverrides ? safeOverrides.dateTo : dateTo;

    if (from) params["date_from"] = from;
    if (to) params["date_to"] = to;

    // Apply search filter
    if (filtersSafe?.search && filtersSafe?.search?.trim()) {
      params.search = filtersSafe.search.trim();
    }

    // Apply ordering filter
    const orderingToUse =
      safeOverrides.ordering ?? filtersSafe?.ordering ?? "-date";
    if (orderingToUse) {
      params.ordering = orderingToUse;
    }

    // Call the service to get the results
    StockService.getAudits(params)
      .then(response => {
        setSubmitted(false);
        setIsScrollingLoading(false);
        document.body.style.cursor = "default";

        const results = Array.isArray(response)
          ? response
          : response?.results || [];

        // const results = response?.results || [];
        const totalCount = response.count || 0;
        const countPages = Math.ceil(totalCount / pageSizeToUse);

        // Set the results
        setLoading(false);
        if (results.length > 0) setNoProducts(false);
        setTotalAudits(totalCount);

        // Update audit items (Infinite scroll for mobile or full replacement for desktop)
        setAuditsItems(prevItems =>
          isAppendMode ? [...prevItems, ...results] : results
        );

        setPager({
          next: response?.next || null,
          previous: response?.previous || null,
          count: totalCount,
          countPages
        });
      })
      .catch(e => {
        setSubmitted(false);
        setIsScrollingLoading(false);
        document.body.style.cursor = "default";
        console.error("Error getting results:", e);
      });
  };

  const handleScroll = useCallback(
    event => {
      if (!isMobile) return;

      const target = event.target;

      if (target.offsetHeight + target.scrollTop + 100 < target.scrollHeight)
        return;

      if (!pager.next || submitted || isScrollingLoading) return;

      setIsScrollingLoading(true);

      setPage(prevPage => {
        const nextPage = prevPage + 1;
        filter(false, { page: nextPage });
        return nextPage;
      });
    },
    [isMobile, pager, submitted, filter, setPage, isScrollingLoading]
  );

  const handlePagerChange = newPage => {
    if (newPage < 1 || newPage > pageQuantity || newPage === currentPage)
      return;
    setPage(newPage);
    filter(false, { page: newPage });
  };

  useEffect(() => {
    BusinessNodeService.businessNodes({ no_page: 1 })
      .then(nodes => {
        const mapped = nodes.map(n => {
          n.id = n.code;
          n.label = n.name;
          n.value = n.name;
          return n;
        });
        const fd = [...filterData];
        fd[0].options = mapped;
        fd[1].options = [];
        fd[2].options = [];
        setFilterData(fd);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const first_load =
      JSON.stringify(initialFilters) === JSON.stringify(filters);
    filter(first_load);
    // Arrange ordering
    const keyOrdering =
      filters.ordering.charAt(0) === "-"
        ? filters.ordering.substr(1)
        : filters.ordering;
    if (keyOrdering in headers) {
      const newHeaders = { ...header };
      setHeader(newHeaders);
    }
  }, []);

  useEffect(() => {
    const currentRef = myRef.current;

    if (isMobile && currentRef) {
      currentRef.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener("scroll", handleScroll, true);
      }
    };
  }, [isMobile, submitted, pager, handleScroll]);

  useEffect(() => {
    if (isFirstLoad) {
      setIsFirstLoad(false);
      return;
    }
    if (suppressFilterEffectRef.current) {
      suppressFilterEffectRef.current = false;
      return;
    }
    filter(false);
    setProductFilters({
      filters: filters,
      selectedFiltersAll: selectedFiltersAll,
      selectedFiltersByType: selectedFiltersByType
    });
  }, [filters]);

  useEffect(() => {
    if (isMobile && isFirstLoad) {
      setMobilePanelOpen(false);
      setIsFirstLoad(false);
    }
    // If it's not mobile or it's not the first charge, don't do anything.
  }, [isMobile, isFirstLoad, setMobilePanelOpen]);

  const handleApplyFilters = arg => {
    let payload = null;
    if (arg && !Array.isArray(arg) && arg.selectedOptions !== undefined) {
      payload = arg;
    }
    const selectedOptionsNew = payload ? payload.selectedOptions : arg;

    setSelectedOptions(selectedOptionsNew || []);

    const statusFromPayload =
      payload && payload.status !== undefined ? payload.status : undefined;
    const modifiedTrueFromPayload =
      payload && typeof payload.modifiedTrue !== "undefined"
        ? payload.modifiedTrue
        : undefined;
    const modifiedFalseFromPayload =
      payload && typeof payload.modifiedFalse !== "undefined"
        ? payload.modifiedFalse
        : undefined;
    const dateFromPayload =
      payload && payload.date_from !== undefined
        ? payload.date_from
        : undefined;
    const dateToPayload =
      payload && payload.date_to !== undefined ? payload.date_to : undefined;

    // Determine the final value to apply (use the payload if present, otherwise the current state)
    const statusToApply =
      typeof statusFromPayload !== "undefined"
        ? statusFromPayload
        : auditStatus;
    const mTrueToApply =
      typeof modifiedTrueFromPayload !== "undefined"
        ? modifiedTrueFromPayload
        : auditModifiedTrue;
    const mFalseToApply =
      typeof modifiedFalseFromPayload !== "undefined"
        ? modifiedFalseFromPayload
        : auditModifiedFalse;
    const dfToApply =
      typeof dateFromPayload !== "undefined" ? dateFromPayload : dateFrom;
    const dtToApply =
      typeof dateToPayload !== "undefined" ? dateToPayload : dateTo;

    // UPDATE LOCAL STATES
    // Ensures filters persist during pagination
    setAuditStatus(statusToApply);
    setAuditModifiedTrue(mTrueToApply);
    setAuditModifiedFalse(mFalseToApply);
    setDateFrom(dfToApply);
    setDateTo(dtToApply);

    setPage(1);

    if (
      !selectedOptionsNew ||
      (Array.isArray(selectedOptionsNew) && selectedOptionsNew.length === 0)
    ) {
      suppressFilterEffectRef.current = true;
      setLocationIds([]);
      filter(true, {
        locations: [],
        status: statusToApply,
        modifiedTrue: mTrueToApply,
        modifiedFalse: mFalseToApply,
        dateFrom: dfToApply,
        dateTo: dtToApply,
        page: 1
      });
      if (isMobile) {
        setMobilePanelOpen(false);
      }
      return;
    }

    // If only the node is selected (without zone/area), expand to locations
    const onlyNodeSelected =
      selectedOptionsNew &&
      selectedOptionsNew[0] &&
      Object.keys(selectedOptionsNew[0]).length > 0 &&
      (!selectedOptionsNew[1] ||
        Object.keys(selectedOptionsNew[1]).length === 0) &&
      (!selectedOptionsNew[2] ||
        Object.keys(selectedOptionsNew[2]).length === 0);

    if (onlyNodeSelected) {
      suppressFilterEffectRef.current = true;
      LocationService.locations({
        code: selectedOptionsNew[0].id,
        no_parent: "true",
        parent: null
      })
        .then(locations => {
          const locationIdsArr = locations.results
            ? locations.results.map(l => l.id)
            : [];
          if (locationIdsArr.length === 0) {
            // Guard clause: Reset state and exit early if no locations are associated with the branch
            setLocationIds([]);
            setNoProducts(true);
            setAuditsItems([]);
            setPager({ next: null, previous: null, count: 0, countPages: 0 });
            setTotalAudits(0);
            if (isMobile) {
              setMobilePanelOpen(false);
            }
            return;
          }
          setLocationIds(locationIdsArr);
          filter(true, {
            locations: locationIdsArr,
            status: statusToApply,
            modifiedTrue: mTrueToApply,
            modifiedFalse: mFalseToApply,
            dateFrom: dfToApply,
            dateTo: dtToApply,
            page: 1
          });
          if (isMobile) {
            setMobilePanelOpen(false);
          }
        })
        .catch(err => {
          console.error("LocationService.locations error:", err);
          // Fallback: reset state to empty values and ensure UI consistency on failure
          setLocationIds([]);
          setNoProducts(true);
          setAuditsItems([]);
          setPager({ next: null, previous: null, count: 0, countPages: 0 });
          setTotalAudits(0);
          if (isMobile) {
            setMobilePanelOpen(false);
          }
        });
      return;
    }

    let locationsParam = [];
    if (
      selectedOptionsNew[2] &&
      Object.keys(selectedOptionsNew[2]).length > 0
    ) {
      locationsParam = [selectedOptionsNew[2].id];
    } else if (
      selectedOptionsNew[1] &&
      Object.keys(selectedOptionsNew[1]).length > 0
    ) {
      locationsParam = [selectedOptionsNew[1].id];
    } else if (
      selectedOptionsNew[0] &&
      Object.keys(selectedOptionsNew[0]).length > 0
    ) {
      locationsParam = [selectedOptionsNew[0].id];
    } else {
      locationsParam = [];
    }

    setLocationIds(locationsParam);

    suppressFilterEffectRef.current = true;
    setLocationIds(locationsParam);
    filter(true, {
      locations: locationsParam,
      status: statusToApply,
      modifiedTrue: mTrueToApply,
      modifiedFalse: mFalseToApply,
      dateFrom: dfToApply,
      dateTo: dtToApply,
      page: 1
    });
    if (isMobile) {
      setMobilePanelOpen(false);
    }
  };

  const clearAllFilters = (payload = {}) => {
    setSelectedOptions([]);
    setLocationIds([]);
    setAuditStatus(undefined);
    setAuditModifiedTrue(false);
    setAuditModifiedFalse(false);
    setDateFrom(null);
    setDateTo(null);
    if (typeof setFilters === "function") {
      try {
        setFilters(initialFilters);
      } catch (e) {}
    }

    suppressFilterEffectRef.current = true;
    setPage(1);

    const overrides = {
      locations: [],
      status: undefined,
      modifiedTrue: false,
      modifiedFalse: false,
      dateFrom: null,
      dateTo: null,
      search: "",
      page: 1
    };
    filter(true, overrides);
    if (isMobile) {
      setMobilePanelOpen(false);
    }
  };

  const handleSelectionChange = (option, label) => {
    if (!option) return;

    setSelectedOptions(prev => {
      const newSel = Array.isArray(prev) ? [...prev] : [{}, {}, {}];

      if (label && filters && Array.isArray(filters)) {
        const idx = filters.findIndex(f => f.label === label);
        if (idx >= 0) {
          newSel[idx] = option;
          return newSel;
        }
      }

      if (option.code) {
        newSel[0] = option;
        return newSel;
      }

      if (option.id) {
        // if there is already a branch in newSel[0], place in the next
        if (!newSel[0] || Object.keys(newSel[0]).length === 0) {
          newSel[0] = option;
        } else if (!newSel[1] || Object.keys(newSel[1]).length === 0) {
          newSel[1] = option;
        } else {
          newSel[2] = option;
        }
        return newSel;
      }

      // fallback: put in the first empty position
      for (let i = 0; i < 3; i++) {
        if (!newSel[i] || Object.keys(newSel[i]).length === 0) {
          newSel[i] = option;
          break;
        }
      }
      return newSel;
    });
  };

  const headerOrderingHandler = column => {
    const sortableColumns = [
      "date",
      "locations",
      "path_name",
      "user__username",
      "status"
    ];

    if (!sortableColumns.includes(column)) return; // ignore the others

    // Change the order of the clicked column
    setHeader(prevState => {
      const currentDirection = prevState[column];
      let newDirection;

      if (currentDirection === undefined || currentDirection === null) {
        newDirection = "ASC";
      } else if (currentDirection === "ASC") {
        newDirection = "DESC";
      } else {
        newDirection = null; // return to "default" state
      }

      const newHeaders = {};
      sortableColumns.forEach(col => {
        newHeaders[col] = col === column ? newDirection : null;
      });

      let orderingParam = "-date";

      if (newDirection === "ASC") {
        orderingParam = column;
      } else if (newDirection === "DESC") {
        orderingParam = `-${column}`;
      }

      // Set the ordering filter with the correct parameter
      setFilters(prevFilters => ({
        ...prevFilters,
        ordering: orderingParam // Set the ordering parameter
      }));

      // Update visual state
      setHeader(newHeaders);

      return newHeaders;
    });
  };

  const downloadGeneralReport = async () => {
    if (submitted || isExcelDisabled) return;

    try {
      const params = {};
      if (locationIds && locationIds.length > 0)
        params["locations"] = locationIds.join(",");
      if (auditStatus !== undefined) params["status"] = auditStatus;

      if (auditModifiedTrue !== auditModifiedFalse) {
        params["modified_status"] = auditModifiedTrue ? true : false;
      }

      if (dateFrom) params["date_from"] = dateFrom;
      if (dateTo) params["date_to"] = dateTo;
      if (filtersSafe?.search) params["search"] = filtersSafe.search.trim();
      if (filtersSafe?.ordering) params["ordering"] = filtersSafe.ordering;

      const blob = await StockService.downloadAuditsListExcel(params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const dateStr = new Date().toISOString().slice(0, 10);
      const fileNameBase = i18n.t("audit.list.file");
      link.setAttribute("download", `${fileNameBase}_${dateStr}.xlsx`);

      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      EventRepository.notificationSend({
        label: i18n.t("success.download"),
        type: "success"
      });
    } catch (error) {
      EventRepository.notificationSend({
        label: i18n.t("error.downloading.report"),
        type: "error"
      });
    }
  };

  const goToProductDetailPage = event => {
    history.push(
      `/admin/products/details/${event.currentTarget.dataset.productId}`
    );
  };

  const headerOptions = [];

  const headerOptionsIcons = [];

  const msgTooltip = i18n.t("download.excel.report");

  const isExcelDisabled = loading || submitted || auditsItems.length === 0;

  const headerButtons = (
    <Tooltip
      content={
        <div
          className={styles["excel-button"]}
          onClick={isExcelDisabled ? null : downloadGeneralReport}
          style={{
            width: "30px",
            cursor: isExcelDisabled ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            opacity: isExcelDisabled ? "0.4" : "1",
            transition: "opacity 0.3s ease"
          }}
        >
          <img
            style={{ height: "30px" }}
            src={isMobileDevice ? excelwhite : excel}
            alt="Excel Icon"
          />
        </div>
      }
      text={isExcelDisabled ? i18n.t("no.data.to.export") : msgTooltip}
    />
  );

  const tableContentDesktop = (
    <React.Fragment>
      <div className={styles["auditoria-de-stock-content"]}>
        <div className={styles["auditoria-de-stock-filter-panel"]}>
          <AuditoriaFilterPanel
            activeStatus={auditStatus}
            activeModifiedTrue={auditModifiedTrue}
            activeModifiedFalse={auditModifiedFalse}
            activeDateFrom={dateFrom}
            activeDateTo={dateTo}
            resultList={products_name}
            placeholder={i18n.t("search.by.name.or.code")}
            searchValue={filters.search}
            handleSubmitSearch={handleSearch}
            filters={filterData}
            selectedFiltersAll={selectedFiltersAll}
            selectedFiltersByType={selectedFiltersByType}
            handleApplyProp={handleApplyFilters}
            handleSelectionChange={handleSelectionChange}
            handleDeleteProp={payload => {
              // payload from panel indicates explicit clear; call centralized clearAllFilters once
              clearAllFilters(payload);
            }}
            fixedButtons={true}
            showFilters={true}
            showModifiedFilter={true}
            showStatusFilter={true}
            showDateFilter={true}
            handleModifiedChange={(mTrue, mFalse) => {
              setAuditModifiedTrue(mTrue);
              setAuditModifiedFalse(mFalse);
            }}
            handleStatusChange={newStatus => {
              setAuditStatus(newStatus);
            }}
            handleDateRangeChange={({ start, end }) => {
              setDateFrom(start);
              setDateTo(end);
            }}
            setPage={setPage}
          />
        </div>
      </div>

      {/* Table component */}
      {auditsItems.length > 0 ? (
        <div className={styles["table-products-table"]}>
          <div className={styles["table-products-top-table"]}>
            <div className={styles["table-products-top-total"]}>
              {i18n.t("total.inventories")}{" "}
              <span className={styles["table-products-top-total-bold"]}>
                ({totalAudits})
              </span>
            </div>
            <div className={styles["table-products-top-sort"]}>
              <div
                data-automation-id="table-products-sort"
                className={
                  filtersSafe.ordering && filtersSafe.ordering.includes("date")
                    ? `${styles["table-products-top-sort"]} ${styles["table-products-active-filter"]}`
                    : styles["table-products-top-sort"]
                }
                onClick={() => {
                  handleFiltersChangeOrdering(
                    "ordering",
                    filtersSafe.ordering === "-date" ? "date" : "-date"
                  );
                  resetOrdering();
                }}
              >
                {filters.ordering === "date" ? (
                  <React.Fragment>
                    {i18n.t("less.recent")}{" "}
                    <i
                      className={`icon-chevron  ${styles["table-products-top-icon"]}`}
                    ></i>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {/* {" "}
                    {i18n.t("latest")}{" "}
                    <i
                      className={`icon-chevron-down ${styles["table-products-chevron-down"]}  ${styles["table-products-top-icon"]}`}
                    ></i> */}
                  </React.Fragment>
                )}
              </div>
            </div>
          </div>
          <AuditTable
            audits={auditsItems}
            headers={header}
            headerClickHandler={headerOrderingHandler}
            handleClickAudit={goToProductDetailPage}
          />
        </div>
      ) : (
        !submitted && <NotFoundItems />
      )}

      {auditsItems.length > 0 && (
        <div>
          <Pagination
            pageQuantity={pageQuantity}
            currentPage={currentPage}
            pageChangeHandler={handlePagerChange}
          />
        </div>
      )}
    </React.Fragment>
  );

  const commonFilterProps = {
    handleOpen: () => setMobilePanelOpen(true),
    activeStatus: auditStatus,
    activeModifiedTrue: auditModifiedTrue,
    activeModifiedFalse: auditModifiedFalse,
    activeDateFrom: dateFrom,
    activeDateTo: dateTo,
    resultList: products_name,
    placeholder: i18n.t("search.by.name.or.code"),
    searchValue: filters.search,
    handleSubmitSearch: handleSearch,
    filters: filterData,
    selectedFiltersAll: selectedFiltersAll,
    selectedFiltersByType: selectedFiltersByType,
    handleApplyProp: handleApplyFilters,
    handleDeleteProp: clearAllFilters,
    setPage: setPage,
    closeMobilePanel: () => {
      setMobilePanelOpen(false);
    },
    showModifiedFilter: true,
    showStatusFilter: true,
    showDateFilter: true,
    showFilters: true
  };

  const tableContentMobile = (
    <React.Fragment>
      {mobilePanelOpen ? (
        <div
          className={`${styles["table-products-mobile-panel"]} ${styles["opened"]}`}
        >
          <AuditoriaFilterPanel opened={true} {...commonFilterProps} />
        </div>
      ) : (
        <AuditoriaFilterPanel
          opened={false}
          className={styles["auditoria-filter-summary"]}
          {...commonFilterProps}
        />
      )}
      <div className={styles["table-products-table"]}>
        <div className={styles["table-products-top-table"]}>
          <div className={styles["table-products-top-total"]}>
            {i18n.t("total.inventories")}{" "}
            <span className={styles["table-products-top-total-bold"]}>
              ({totalAudits})
            </span>
          </div>
          <div
            className={`${styles["table-products-top-sort"]} ${
              applied ? styles["table-products-active-filter"] : ""
            }`}
            onClick={() => {
              setMobilePanelOpen(true);
            }}
          >
            <i
              className={`icon-iconos_filtros ${styles[" table-products-top-icon"]}`}
            ></i>
          </div>
        </div>

        {getFilterSummaryText()}

        {auditsItems.length > 0 ? (
          <AuditTable
            audits={auditsItems}
            headers={header}
            handleClickAudit={goToProductDetailPage}
          />
        ) : (
          !submitted && <NotFoundItems />
        )}

        {submitted && pager?.next && (
          <div className={styles["bottom-scroll-loader"]}>
            <Loader />
          </div>
        )}
      </div>

      {items.length === 0 && !submitted && <NotFoundItems />}
    </React.Fragment>
  );

  const tableContent = isMobile ? tableContentMobile : tableContentDesktop;

  const content = (
    <div className={styles["table-products-wrapper"]} ref={myRef}>
      {tableContent}

      {submitted && auditsItems.length === 0 && (
        <div className={styles["full-list-loader-overlay"]}>
          <Loader />
        </div>
      )}

      {loadingName && <Loader />}
    </div>
  );

  return (
    <div className={styles["auditoria-de-stock-container"]}>
      {isScrollingLoading && (
        <div className={styles["fixed-viewport-loader"]}>
          <Loader />
        </div>
      )}

      <AdminLayout
        headerTitle={i18n.t("stock.audit")}
        headerOptions={headerOptions}
        headerOptionsIcons={headerOptionsIcons}
        customButtons={headerButtons}
        content={content}
        navHidden={false}
        goBackFunc={() => history.push("/admin/auditoria-de-stock")}
      ></AdminLayout>
      <MainNotification
        label={i18n.t("product.updated.successfully.message")}
        open={false}
      ></MainNotification>
    </div>
  );
};

export default ProductsView;
